import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Gift, Save, Eye, TestTube, X, Calendar, Users, Mail, ToggleLeft, ToggleRight, RefreshCw } from 'lucide-react';
import { toast } from 'react-toastify';
import axios from 'axios';
import { AuthContext } from '../../context/AuthContext.jsx';

// Default birthday email HTML template (uses same structure as campaign emails)
const DEFAULT_BIRTHDAY_HTML = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Happy Birthday</title>
</head>
<body style="margin: 0; padding: 0; font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; background-color: #f4f4f4;">
  <table width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color: #f4f4f4; margin: 0; padding: 0;">
    <tr>
      <td align="center" style="padding: 20px 0;">
        <table cellpadding="0" cellspacing="0" border="0" style="max-width: 600px; width: 100%; background-color: #ffffff; margin: 0 auto;">

          <!-- Header Section -->
          <tr>
            <td style="background: linear-gradient(135deg, #e91e63 0%, #9c27b0 100%); color: #ffffff; padding: 40px 30px; text-align: center;">
              <img src="https://www.ellasmgt.com/logo.png" alt="Ella's Place" style="max-width: 180px; height: auto; margin: 0 auto 20px; display: block;">
              <h1 style="margin: 0; font-size: 32px; font-weight: 700; color: #ffffff;">Happy Birthday! 🎉</h1>
            </td>
          </tr>

          <!-- Content Section -->
          <tr>
            <td style="padding: 40px 30px; color: #333333; line-height: 1.8;">
              <p style="margin: 15px 0; font-size: 16px;">Dear <strong>{{First Name}}</strong>,</p>
              <p style="margin: 15px 0; font-size: 16px;">Happy Birthday! 🎉💖<br>All of us at Ella's Place are wishing you a day filled with joy, confidence, and beautiful moments.</p>
              <p style="margin: 15px 0; font-size: 16px;">Thank you for being part of the Ella's Place family. We're grateful to celebrate you today and we can't wait to keep supporting your fitness and wellness journey.</p>
              <p style="margin: 15px 0; font-size: 16px;">May this new year bring you strength, happiness, and plenty of reasons to smile ✨<br>Have the most amazing birthday!</p>
              <p style="margin: 25px 0 15px 0; font-size: 16px;">With love,<br><strong>Ella's Place Team</strong> 🖤</p>
            </td>
          </tr>

          <!-- Footer Section -->
          <tr>
            <td style="background: linear-gradient(to bottom, #f9f9f9 0%, #f5f5f5 100%); padding: 40px 30px; text-align: center; border-top: 3px solid #e91e63;">
              <div style="width: 80px; height: 2px; background-color: #e91e63; margin: 0 auto 25px; opacity: 0.3;"></div>
              <p style="margin: 0 0 8px 0; font-size: 16px; font-weight: 600; color: #2c3e50; letter-spacing: 0.5px;">Ella's Place</p>
              <p style="margin: 0 0 20px 0; font-size: 11px; font-style: italic; color: #7f8c8d; letter-spacing: 0.3px;">Salon • Spa • Ladies Gym</p>
              <div style="margin: 20px 0; padding: 15px 0; border-top: 1px solid #e0e0e0; border-bottom: 1px solid #e0e0e0;">
                <p style="margin: 5px 0; font-size: 11px; color: #555555;">
                  <span style="display: inline-block; margin: 0 3px;">📞</span>
                  <a href="tel:+2349055444495" style="color: #e91e63; text-decoration: none; font-weight: 500;">+234 905 544 4495</a>
                </p>
                <p style="margin: 5px 0; font-size: 11px; color: #555555;">
                  <span style="display: inline-block; margin: 0 3px;">📧</span>
                  <a href="mailto:info@ellasplace.net" style="color: #e91e63; text-decoration: none; font-weight: 500;">info@ellasplace.net</a>
                </p>
                <p style="margin: 5px 0; font-size: 11px; color: #555555;">
                  <span style="display: inline-block; margin: 0 3px;">🌐</span>
                  <a href="https://www.ellasplace.net" style="color: #e91e63; text-decoration: none; font-weight: 500;">www.ellasplace.net</a>
                </p>
              </div>
              <p style="margin: 20px 0 15px 0; font-size: 10px; font-style: italic; color: #95a5a6; line-height: 1.5;">
                Sent with love from Ella's Place ❤️
              </p>
              <p style="margin: 15px 0 0 0; font-size: 10px; color: #bdc3c7;">
                <a href="{{unsubscribeLink}}" style="color: #7f8c8d; text-decoration: underline; font-style: italic;">Unsubscribe</a> from marketing emails
              </p>
              <p style="margin: 15px 0 0 0; font-size: 9px; color: #bdc3c7; font-style: italic;">
                © ${new Date().getFullYear()} Ella's Place. All rights reserved.
              </p>
            </td>
          </tr>

        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;

const BirthdayMessages = () => {
  const navigate = useNavigate();
  const { logout } = useContext(AuthContext);

  // State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [subject, setSubject] = useState("Happy Birthday {{First Name}}! 🎉 From Ella's Place");
  const [messageHtml, setMessageHtml] = useState(DEFAULT_BIRTHDAY_HTML);
  const [showPreview, setShowPreview] = useState(false);
  const [showTestModal, setShowTestModal] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testName, setTestName] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [upcoming, setUpcoming] = useState([]);
  const [totalUpcoming, setTotalUpcoming] = useState(0);
  const [sendLog, setSendLog] = useState([]);
  const [totalSent, setTotalSent] = useState(0);

  // Fetch birthday settings
  const fetchSettings = useCallback(async () => {
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/api/comms-marketing/birthday-settings`
      );
      const settings = response.data.birthdaySettings || {};
      setEnabled(settings.enabled || false);
      setSubject(settings.subject || "Happy Birthday from Ella's Place 🎉");
      if (settings.messageHtml) {
        setMessageHtml(settings.messageHtml);
      }
    } catch (err) {
      console.error('Error fetching birthday settings:', err);
      if (err.response?.status === 401) {
        logout();
        navigate('/login');
      }
    }
  }, []);

  // Fetch upcoming birthdays
  const fetchUpcoming = useCallback(async () => {
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/api/comms-marketing/birthday-upcoming`
      );
      setUpcoming(response.data.upcoming || []);
      setTotalUpcoming(response.data.totalUpcoming || 0);
    } catch (err) {
      console.error('Error fetching upcoming birthdays:', err);
    }
  }, []);

  // Fetch send log
  const fetchLog = useCallback(async () => {
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}/api/comms-marketing/birthday-log`
      );
      setSendLog(response.data.log || []);
      setTotalSent(response.data.totalSent || 0);
    } catch (err) {
      console.error('Error fetching birthday log:', err);
    }
  }, []);

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([fetchSettings(), fetchUpcoming(), fetchLog()]);
      setLoading(false);
    };
    loadAll();
  }, [fetchSettings, fetchUpcoming, fetchLog]);

  // Save settings
  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${import.meta.env.VITE_API_URL}/api/comms-marketing/birthday-settings`,
        { enabled, subject, messageHtml }
      );
      toast.success('Birthday settings saved!');
    } catch (err) {
      console.error('Error saving birthday settings:', err);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // Toggle enabled
  const handleToggle = async () => {
    const newEnabled = !enabled;
    setEnabled(newEnabled);
    try {
      await axios.put(
        `${import.meta.env.VITE_API_URL}/api/comms-marketing/birthday-settings`,
        { enabled: newEnabled, subject, messageHtml }
      );
      toast.success(newEnabled ? 'Birthday emails enabled!' : 'Birthday emails disabled');
    } catch (err) {
      setEnabled(!newEnabled); // revert
      toast.error('Failed to update setting');
    }
  };

  // Send test email
  const handleSendTest = async () => {
    if (!testEmail) {
      toast.error('Please enter a test email');
      return;
    }
    setSendingTest(true);
    try {
      await axios.post(
        `${import.meta.env.VITE_API_URL}/api/comms-marketing/birthday-send-test`,
        { toEmail: testEmail, toName: testName || 'Test User' }
      );
      toast.success(`Test birthday email sent to ${testEmail}!`);
      setShowTestModal(false);
      setTestEmail('');
      setTestName('');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send test email');
    } finally {
      setSendingTest(false);
    }
  };

  // Format date for display
  const formatBirthdayDate = (mmdd) => {
    const [month, day] = mmdd.split('-');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[parseInt(month) - 1]} ${parseInt(day)}`;
  };

  // Render preview with sample data
  const renderPreview = () => {
    let preview = messageHtml;
    preview = preview.replace(/\{\{First Name\}\}/g, 'Jane');
    preview = preview.replace(/\{\{name\}\}/g, 'Jane Doe');
    preview = preview.replace(/\{\{email\}\}/g, 'jane@example.com');
    preview = preview.replace(/\{\{unsubscribeLink\}\}/g, '#');
    return preview;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <div className="bg-gradient-to-r from-pink-600 via-purple-600 to-pink-700 rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center">
              <Gift className="w-7 h-7 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">Birthday Messages</h2>
              <p className="text-white/80 text-sm mt-1">
                Automated birthday emails sent daily at midnight
              </p>
            </div>
          </div>
          <button
            onClick={handleToggle}
            className={`flex items-center gap-2 px-5 py-3 rounded-lg font-semibold text-sm transition ${
              enabled
                ? 'bg-green-500 text-white hover:bg-green-600'
                : 'bg-white/20 text-white hover:bg-white/30'
            }`}
          >
            {enabled ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
            {enabled ? 'Enabled' : 'Disabled'}
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow-md p-4">
          <h3 className="text-sm font-semibold text-gray-600 mb-1">Status</h3>
          <div className="flex items-center gap-2">
            <div className={`w-3 h-3 rounded-full ${enabled ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`}></div>
            <span className={`text-lg font-bold ${enabled ? 'text-green-600' : 'text-gray-500'}`}>
              {enabled ? 'Active' : 'Inactive'}
            </span>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            {enabled ? 'Runs daily at 12:00 AM' : 'No birthday emails being sent'}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow-md p-4">
          <h3 className="text-sm font-semibold text-gray-600 mb-1">Upcoming Birthdays</h3>
          <p className="text-2xl font-bold text-purple-600">{totalUpcoming}</p>
          <p className="text-xs text-gray-500 mt-1">In the next 7 days</p>
        </div>
        <div className="bg-white rounded-lg shadow-md p-4">
          <h3 className="text-sm font-semibold text-gray-600 mb-1">Total Sent</h3>
          <p className="text-2xl font-bold text-pink-600">{totalSent}</p>
          <p className="text-xs text-gray-500 mt-1">Birthday emails all time</p>
        </div>
      </div>

      {/* Message Editor Section */}
      <div className="bg-white rounded-lg shadow-md">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Birthday Email Template</h3>
            <div className="flex gap-2">
              <button
                onClick={() => setShowPreview(true)}
                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition text-sm flex items-center gap-2"
              >
                <Eye size={16} />
                Preview
              </button>
              <button
                onClick={() => setShowTestModal(true)}
                className="px-4 py-2 bg-gradient-to-r from-orange-500 to-yellow-500 text-white rounded-md hover:shadow-lg transition text-sm flex items-center gap-2"
              >
                <TestTube size={16} />
                Send Test
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition text-sm flex items-center gap-2 disabled:opacity-50"
              >
                {saving ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <Save size={16} />
                )}
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {/* Subject */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email Subject</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500"
              placeholder="Happy Birthday from Ella's Place 🎉"
            />
            <p className="text-xs text-gray-500 mt-1">
              You can use {'{{First Name}}'} in the subject line
            </p>
          </div>

          {/* Visual Preview */}
          <div className="border border-gray-300 rounded-lg overflow-hidden bg-gray-50">
            <div className="bg-gray-100 px-3 py-2 border-b border-gray-300">
              <span className="text-xs font-semibold text-gray-700">Email Preview</span>
            </div>
            <div className="p-4" style={{ maxHeight: '600px', overflowY: 'auto' }}>
              <iframe
                srcDoc={renderPreview()}
                className="w-full border-0 bg-white"
                style={{ minHeight: '500px' }}
                title="Birthday Email Preview"
              />
            </div>
          </div>

          {/* Edit Notice */}
          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md">
            <p className="text-sm text-yellow-800">
              To edit the birthday message template, please contact admin.
            </p>
          </div>

          {/* Info Box */}
          <div className="p-3 bg-pink-50 border border-pink-200 rounded-md">
            <p className="text-sm text-pink-800">
              <strong>How it works:</strong> When enabled, the system automatically checks for customer birthdays
              every day at midnight. Customers whose birthday matches today's date (month & day) will receive this
              personalized email. Only customers with valid emails and marketing consent will be emailed.
            </p>
          </div>
        </div>
      </div>

      {/* Upcoming Birthdays */}
      <div className="bg-white rounded-lg shadow-md">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Calendar size={20} className="text-purple-600" />
            Upcoming Birthdays (Next 7 Days)
          </h3>
          <button
            onClick={fetchUpcoming}
            className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
          >
            <RefreshCw size={14} />
            Refresh
          </button>
        </div>
        <div className="p-4">
          {upcoming.length > 0 ? (
            <div className="space-y-3">
              {upcoming.map((group, idx) => (
                <div key={idx} className="border border-gray-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                      group.isToday ? 'bg-pink-100 text-pink-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {group.isToday ? 'Today' : formatBirthdayDate(group.date)}
                    </span>
                    <span className="text-xs text-gray-500">{group.customers.length} customer(s)</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {group.customers.map((customer, cIdx) => (
                      <div key={cIdx} className="flex items-center gap-3 p-2 bg-gray-50 rounded-md">
                        <div className="w-8 h-8 bg-pink-100 rounded-full flex items-center justify-center text-pink-600 text-sm font-bold">
                          {customer.name?.charAt(0)}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-800">{customer.name}</p>
                          <p className="text-xs text-gray-500">{customer.email || 'No email'}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Calendar size={40} className="mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500">No upcoming birthdays in the next 7 days</p>
            </div>
          )}
        </div>
      </div>

      {/* Send History */}
      <div className="bg-white rounded-lg shadow-md">
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Mail size={20} className="text-green-600" />
            Recent Birthday Emails Sent
          </h3>
        </div>
        <div className="p-4">
          {sendLog.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sent</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Recipients</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {sendLog.map((entry, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {new Date(entry.date).toLocaleDateString('en-US', {
                          weekday: 'short', year: 'numeric', month: 'short', day: 'numeric'
                        })}
                      </td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">
                          {entry.recipientCount} email(s)
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {entry.recipients?.map(r => r.name).join(', ') || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8">
              <Mail size={40} className="mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500">No birthday emails sent yet</p>
              <p className="text-xs text-gray-400 mt-1">
                {enabled ? 'Emails will be sent automatically when customers have birthdays' : 'Enable the birthday system to start sending'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b flex items-center justify-between bg-gradient-to-r from-pink-600 to-purple-600">
              <h2 className="text-lg font-bold text-white">Birthday Email Preview</h2>
              <button onClick={() => setShowPreview(false)} className="text-white hover:text-gray-200">
                <X size={24} />
              </button>
            </div>
            <div className="overflow-y-auto flex-1 p-6 bg-gray-50">
              <div className="bg-white rounded-lg shadow-lg max-w-2xl mx-auto">
                <div className="p-4 border-b bg-gray-50">
                  <div className="text-sm text-gray-600 mb-1">
                    <strong>From:</strong> Ella's Place &lt;info@ellasplace.net&gt;
                  </div>
                  <div className="text-sm text-gray-600 mb-1">
                    <strong>Subject:</strong> {subject.replace(/\{\{First Name\}\}/g, 'Jane')}
                  </div>
                </div>
                <div className="p-4">
                  <iframe
                    srcDoc={renderPreview()}
                    className="w-full border-none"
                    style={{ minHeight: '500px' }}
                    title="Birthday Preview"
                  />
                </div>
              </div>
            </div>
            <div className="p-4 border-t flex justify-end">
              <button
                onClick={() => setShowPreview(false)}
                className="px-4 py-2 bg-pink-600 text-white rounded-md hover:bg-pink-700 transition"
              >
                Close Preview
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Test Email Modal */}
      {showTestModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-md">
            <div className="p-4 border-b bg-gradient-to-r from-orange-500 to-yellow-500 flex items-center justify-between">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <TestTube size={20} />
                Send Test Birthday Email
              </h3>
              <button onClick={() => setShowTestModal(false)} className="text-white hover:text-gray-200" disabled={sendingTest}>
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-600">
                Send a test birthday email to verify the template looks correct.
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Test Email *</label>
                <input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="test@example.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  disabled={sendingTest}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Recipient Name</label>
                <input
                  type="text"
                  value={testName}
                  onChange={(e) => setTestName(e.target.value)}
                  placeholder="Jane Doe"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  disabled={sendingTest}
                />
                <p className="text-xs text-gray-500 mt-1">Will replace {'{{First Name}}'} in the email</p>
              </div>
            </div>
            <div className="p-4 border-t bg-gray-50 flex justify-end gap-3">
              <button
                onClick={() => setShowTestModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition"
                disabled={sendingTest}
              >
                Cancel
              </button>
              <button
                onClick={handleSendTest}
                disabled={sendingTest || !testEmail}
                className="px-6 py-2 bg-gradient-to-r from-orange-500 to-yellow-500 text-white rounded-md hover:shadow-lg transition flex items-center gap-2 disabled:opacity-50"
              >
                {sendingTest ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Sending...
                  </>
                ) : (
                  <>
                    <TestTube size={16} />
                    Send Test
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BirthdayMessages;
